import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useClientData } from '../../../providers/ClientData.provider';
import useFetchAutoBalanceTransfer from '../../../hooks/useApi/useFetchAutoBalanceTransfer';
import usePostCancelAutoBalanceTransfer from '../../../hooks/useApi/usePostCancelAutoBalanceTransfer';

import AutoBalanceTransferDialogue from './Dialogue/CancelTransfer/AutoBalanceTransferDialogue';
import AutoBalanceTransferContext, {
  AutoBalanceTransferCtx,
} from './Dialogue/CancelTransfer/AutoBalanceTransferContext';
import { AutoBalanceTransferDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import TransferOverviewTable from './TransferOverviewTable';
import { useTranslation } from 'react-i18next';

const { Box, Title, Button } = vp;

type FC = FunctionComponent;

const TransferOverviewContainer: FC = () => {
  const history = useHistory();
  const { response } = useClientData();
  const [autoBalanceTransferResponse, fetchAutoBalanceTransfer] = useFetchAutoBalanceTransfer({});
  const [cancelAutoBalanceTransfer, postCancelAutoBalanceTransfer] = usePostCancelAutoBalanceTransfer({});

  const [showCancelAutoBalanceTransferConfirmAlert, setShowCancelAutoBalanceTransferConfirmAlert] = useState(false);
  const [showCancelAutoBalanceTransferSuccessAlert, setShowCancelAutoBalanceTransferSuccessAlert] = useState(false);
  const [showCancelAutoBalanceTransferErrorAlert, setShowCancelAutoBalanceTransferErrorAlert] = useState(false);
  const [cancelAutoBalanceTransferError, setCancelAutoBalanceTransferError] = useState('');
  const [shouldCancelAutoBalanceTransfer, setShouldCancelAutoBalanceTransfer] = useState(false);

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer' });

  const CancelAutoBalanceTransfer: AutoBalanceTransferCtx = {
    showCancelAutoBalanceTransferConfirmAlert,
    showCancelAutoBalanceTransferErrorAlert,
    showCancelAutoBalanceTransferSuccessAlert,
    cancelAutoBalanceTransferError,
  };

  const handlers = {
    handleAutoBalanceTransferDialogue: (handlerProps: DialogueHandlerProps<AutoBalanceTransferDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: AutoBalanceTransferDialogueHandler = {
        cancel: {
          confirm: {
            default: () => {
              setShowCancelAutoBalanceTransferSuccessAlert(false);
              history.push('/auto-balance-transfer');
            },
          },
          fail: {
            default: () => {
              setShowCancelAutoBalanceTransferErrorAlert(false);
              history.push('/auto-balance-transfer');
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowCancelAutoBalanceTransferConfirmAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowCancelAutoBalanceTransferConfirmAlert(false);
              setShouldCancelAutoBalanceTransfer(true);
              if (response.Success) {
                postCancelAutoBalanceTransfer({
                  PortalUser: response.LoginUserName,
                });
              }
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    fetchAutoBalanceTransfer({
      PortalUser: response.LoginUserName,
    });
  }, []);

  useEffect(() => {
    if (shouldCancelAutoBalanceTransfer) {
      setShouldCancelAutoBalanceTransfer(false);
      const { Success, ErrorMessage } = cancelAutoBalanceTransfer;

      if (Success === true) {
        setShowCancelAutoBalanceTransferSuccessAlert(true);
      } else {
        setShowCancelAutoBalanceTransferErrorAlert(true);
        setCancelAutoBalanceTransferError(ErrorMessage);
      }
    }
  }, [cancelAutoBalanceTransfer]);

  return (
    <AutoBalanceTransferContext.Provider value={CancelAutoBalanceTransfer}>
      <Box mb="md" jc="sb">
        <Box mb="md" jc="sb">
          <Title title={t('title')} />
        </Box>
        <Box gap="sm">
          <Button
            id="update-balance-transfer-button"
            label={t('Overview.Buttons.update')}
            onClick={() => {
              history.push({
                pathname: '/update-auto-balance-transfer',
                state: { title: 'Update' },
              });
            }}
          />
          <Button
            id="cancel-balance-transfer-button"
            label={t('Overview.Buttons.cancel')}
            color="secondary"
            onClick={() => {
              setShowCancelAutoBalanceTransferConfirmAlert(true);
            }}
          />
        </Box>
      </Box>
      <TransferOverviewTable autoBalanceTransferResponse={autoBalanceTransferResponse} />
      <AutoBalanceTransferDialogue handleAutoBalanceTransferDialogue={handlers.handleAutoBalanceTransferDialogue} />
    </AutoBalanceTransferContext.Provider>
  );
};

export default TransferOverviewContainer;
